import Vue from 'vue';
import { captureException, captureEvent } from '@sentry/browser';

import { Root } from './views/shared';
import {
    makeCasting,
    makeCore,
    makeProject,
    makeSolution,
    makeUser,
    makeTracking,
    makeSkeepersTracking,
    makeGoogleTracking,
    makeTutorial,
} from './store';
import {
    useClamp,
    useRouter,
    useSentry,
    useUI,
    useVuex,
} from './plugins';
import { noop, device, isFromRouter } from './utils';
import { loadCSATModule } from './ifmCSATModule';

// Styles
import './assets/scss/main.scss';

/**
 * Promise corresponding to the app initialization
 */
let initPromise = null;

function setupRouter (options) {
    const { store, router } = options;

    console.log('[Setup 3/4] Setup router');

    // On mobile devices, scroll to top at each navigation
    if (device.isMobile) {
        router.options.scrollBehavior = (to, from, savedPosition) => {
            if (savedPosition) {
                return savedPosition;
            } else {
                return { x: 0, y: 0 };
            }
        };
    }

    // On error during navigation
    router.onError((err) => {
        // If Sentry is set up, send the error
        captureException(err);
        console.error(err);
    });

    // Prevent further hooks to be called unless store is correctly initialized,
    // because they depend on store data such as user or tracking info. If an
    // error occurs during store init, abort navigation
    router.beforeEach((to, from, next) => {
        initPromise.then(next).catch(next);
    });

    // Auth guard
    router.beforeEach((to, from, next) => {
        // Stop here if user is not logged in although they should.
        if (to.matched.some((route) => route.meta.auth)) {
            const { grant, reason } = store.getters['core/shouldGrantAccess'];

            console.log(`[Router] Trying to navigate to protected route ${to.name}...`);

            if (!grant) {
                console.log(`[Router] Access denied: ${reason}, redirecting...`);
                // Log forbidden events
                captureEvent({
                    message: 'Access denied',
                    level: 'debug',
                    extra: {
                        solutionId: store.state.solution.id,
                        castingId: store.state.solution.config.CASTING,
                        application: store.state.casting.application,
                        reason,
                    },
                });
                next(store.getters['core/forbidden']);
            } else {
                console.log(`[Router] Access granted: ${reason}`);
                next();
            }
        } else {
            next();
        }
    });

    // Tracking
    router.afterEach((to) => {
        // Get the most relevant event to track: the last child from the matched descendants.
        // This is used for abstract parent routes that redirect to one of their child but
        // contain the event themselves.
        const events = to.matched.map((route) => route.meta.tracking).filter((event) => !!event);
        const event = events[events.length - 1];

        if (event) {
            store.dispatch('tracking/teester/trackEvent', event);
        }
    });
}

function setupStore (options) {
    const {
        api,
        playerEmbedUrl,
        playerFrontUrl,
        connectFrontUrl,
        mediainfoUrl,
        imageUrl,
        store,
        router,
        onSetupApp = noop,
        onSetupUser = noop,
        onLogin = noop,
        onUpload = noop,
        makeRoutes = () => [],
        welcome,
        home = '/',
        forbidden = '/',
        uploadOptions = {
            validTypes: ['IMAGE', 'VIDEO', 'AUDIO'],
            multiple: true,
        },
    } = options;
    const makeWelcome = ((typeof welcome === 'function') ? welcome : () => welcome || '/');

    console.log('[Setup 2/4] Setup store');

    // Register store modules
    store.registerModule('casting', makeCasting({ api }));
    store.registerModule('core', makeCore({
        api,
        makeRoutes,
        makeWelcome,
        home,
        forbidden,
        onSetupApp,
        mediainfoUrl,
        uploadOptions,
        imageUrl,
    }));
    store.registerModule('project', makeProject({
        api,
        playerFrontUrl,
        playerEmbedUrl,
        onUpload,
    }));
    store.registerModule('solution', makeSolution({ api }));
    store.registerModule('tracking', makeTracking());
    store.registerModule('tracking/google', makeGoogleTracking());
    store.registerModule('tracking/teester', makeSkeepersTracking({ api }));
    store.registerModule('tutorial', makeTutorial());
    store.registerModule('user', makeUser({
        api,
        router,
        connectFrontUrl,
        onSetupUser,
        onLogin,
    }));
}

function setupPlugins (options) {
    const { sentryDsn } = options;

    console.log('[Setup 1/4] Setup plugins');

    useClamp();
    useRouter();
    useSentry(sentryDsn);
    useUI();
    useVuex();
}

/**
 * Platform factory. Sets up the platform, including store module and hooks.
 * Returns the app which is ready to be mounted.
 * @param {object} options
 * @param {string} options.name - App name
 * @param {API} options.api - API instance. Must inherit from API
 * @param {string} options.sentryDsn - Sentry DSN to send the error logs to
 * @param {Vuex.Store} options.store - Vuex base store instance
 * @param {VueRouter} options.router - Vue router instance
 * @param {string} options.playerEmbedUrl - URL to the embedded player
 * @param {string} options.ifmCsatModule - URL to the csat module from ifm
 * @param {string} options.playerFrontUrl - URL to the player front page
 * @param {string} options.connectFrontUrl - URL to the connect front page
 * @param {string | object | function} options.home - homepage
 * @param {string | object | function} options.welcome - computed welcome page to be redirected to as base page when logging in for example
 * @param {function} options.onSetupUser - function to complete the user setup
 * @param {function} options.onSetupApp - function to complete the app setup
 * @param {function} options.onUpload - function called after a project has been created, i.e. the user has uploaded their video
 * @param {function} options.onLogin - function called after a user logged in, i.e. they just came back from the connect front
 * @returns {Promise<Vue>} a ready-to-use Vue app
 */
export async function makeApp (options) {
    const { name, store, router, ifmCsatUrl } = options;

    if (process.env.NODE_ENV === 'development') {
        console.log(device.isMobile ? 'Mobile detected' : 'Desktop detected');
        console.log(isFromRouter ? 'From router' : 'Direct access');
    }

    try {
        // Prepare store and router, and install plugins
        setupPlugins(options);
        setupStore(options);
        setupRouter(options);

        if (ifmCsatUrl) {
            loadCSATModule(ifmCsatUrl);
        }

        // Init application
        initPromise = store.dispatch('core/init');

        return new Vue({
            name,
            store,
            router,
            render: (h) => h(Root),
        });
    } catch (err) {
        // If Sentry is set up, send the error
        captureException(err);
        // Forward the error
        throw err;
    }
}

/**
 * Predefined actions to perform on app ready / error, depending on execution environment
 */
export const { onReady = noop, onError = noop } = process.env.NODE_ENV === 'production' ? window.teester : {
    onReady: (app) => app.$mount('#app'),
    onError: console.error,
};

export * from './utils';
export * from './data';
